import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { ArrowForward } from "@mui/icons-material";
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation } from 'react-router-dom';
import { 
  fetchJobs, 
  selectJobById, 
  selectJobsStatus, 
  selectJobsError 
} from '../../store/jobsSlice';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const JDPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  // Redux state
  const job = useSelector(state => selectJobById(state, id));
  const status = useSelector(selectJobsStatus);
  const error = useSelector(selectJobsError);

  useEffect(() => {
    if (status === 'idle') {
      // Only fetch the specific job needed for this page
      dispatch(fetchJobs({ singleJobId: id }));
    }
  }, [status, dispatch, id]);
  useEffect(() => {
    // Defer storage operations to avoid blocking main thread
    setTimeout(() => {
      const shouldScroll = sessionStorage.getItem('scrollToOpenRoles') === 'true';
      
      if (shouldScroll) {
        sessionStorage.removeItem('scrollToOpenRoles');
        
        // Use requestAnimationFrame for smoother scrolling
        requestAnimationFrame(() => {
          setTimeout(() => {
            const rolesSection = document.getElementById('open-roles-section');
            if (rolesSection) {
              rolesSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        });
      }
    }, 0);
  }, []);

  const handleBackClick = () => {
    // Defer storage operation
    setTimeout(() => {
      localStorage.setItem('shouldScrollToJobs', 'true');
    }, 0);
    
    navigate('/joinus');
  };

  // Optimized text styles
  const textStyle = {
    fontWeight: 300,
    fontSize: { xs: '14px', sm: '16px', md: '18px' },
    color: '#555555',
    lineHeight: 1.6
  };

  // Pre-calculated responsive font sizes
  const titleFontSize = { xs: '2.25rem', sm: '3.25rem', md: '4.5rem' };

  // Skeleton Component for better loading experience
  const JobSkeleton = () => (
    <Container maxWidth="md" sx={{ mt: 20, mb: 5, p: 3 }}>
      {/* Back button skeleton */}
      <Skeleton 
        variant="circular" 
        width={40} 
        height={40} 
        sx={{ mb: 6 }} 
      />
      
      {/* Title skeleton */}
      <Skeleton 
        variant="text" 
        width="80%" 
        height={80} 
        sx={{ mb: 2 }} 
      />
      
      {/* Location skeleton */}
      <Box sx={{ display: 'flex', mb: 1, alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={25} height={25} />
        <Skeleton variant="text" width="30%" height={20} />
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* About section skeleton */}
      <Box mb={3}>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="95%" />
      </Box>
      
      {/* Responsibilities section skeleton */}
      <Box mb={3}>
        <Skeleton variant="text" width="50%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="85%" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="80%" />
      </Box>
      
      {/* Requirements section skeleton */}
      <Box mb={3}>
        <Skeleton variant="text" width="35%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="88%" />
        <Skeleton variant="text" width="85%" />
        <Skeleton variant="text" width="92%" />
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Apply button skeleton */}
      <Box display="flex" justifyContent="center">
        <Skeleton 
          variant="rectangular" 
          width={150} 
          height={50} 
          sx={{ borderRadius: '50px' }} 
        />
      </Box>
    </Container>
  );

  // Show skeleton while loading or if no job data
  if (status === 'loading' || !job) {
    return <JobSkeleton />;
  }

  if (status === 'failed') {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: '0px', sm: 8, md: 20 }, mb: 5, p: 3, textAlign: 'center' }}>
    <Typography variant="h5" sx={{ color: 'text.primary', mb: 3 }}>
      Oops, we couldn’t find that job description right now!. Please head back to explore other exciting opportunities at DevAngles or try again.
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
      <Button
        variant="contained"
        onClick={handleBackClick}
        sx={{ bgcolor: '#0087B4', color: 'white', borderRadius: '15px', textTransform: 'none', fontWeight: 300 }}
      >
        Back to Jobs
      </Button>
      <Button
        variant="outlined"
        onClick={() => dispatch(fetchJobs())}
        sx={{
          borderColor: '#0087B4',
          color: '#0087B4',
          borderRadius: '15px',
          textTransform: 'none',
          fontWeight: 300,
        }}
      >
        Try Again
      </Button>
    </Box>
  </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 20, mb: 5, p: 3 }}>
      <IconButton 
        onClick={handleBackClick}
        color="#0087B4" 
        aria-label="Go back"
        sx={{
          width: '40px', 
          height: '40px', 
          borderRadius: '50%', 
          border: 'solid 1px #0087B4', 
          mb: 6
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 20, color: '#0087B4' }}/>
      </IconButton>
        
      <Typography 
        variant="h1" 
        component="h1"
        sx={{
          fontSize: titleFontSize,
          fontWeight: 500,
          mb: 2,
          lineHeight: 1.2,
          // Optimize font loading
          fontDisplay: 'swap',
          fontOpticalSizing: 'auto',
  fontVariationSettings: '"opsz" auto'
        }}
      >
        
        {job.JobTitle}
      </Typography>
      
      <Box sx={{ display: 'flex', mb: 1, alignItems: 'center', gap: 1 }}>
        <LocationOnIcon sx={{ color: '#0087B4', fontSize: 25 }} />
        <Typography variant="body1">{job.Location}</Typography>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box mb={3}>
        <Typography 
          variant="h4" 
          component="h2"
          gutterBottom
          sx={{ fontWeight: 500 }}
        >
          About the role
        </Typography>
        <Typography 
          variant="body2" 
          component="p"
          sx={textStyle}
        >
          {job.JobAbout}
        </Typography>
      </Box>
      
      <Box mb={3}>
        <Typography 
          variant="h4" 
          component="h2"
          gutterBottom
          sx={{ fontWeight: 500 }}
        >
          Key responsibilities
        </Typography>
        <List 
          component="ul"
          sx={{ 
            listStyleType: 'disc', 
            ml: 3, 
            ...textStyle 
          }}
        >
          {job.JobResponsibility && job.JobResponsibility.map((item, index) => (
            <ListItem 
              key={index} 
              component="li"
              sx={{ 
                display: 'list-item', 
                pl: 2,
                py: 0.5
              }}
            >
              {item}
            </ListItem>
          ))}
        </List>
      </Box>
      
      <Box mb={3}>
        <Typography 
          variant="h4" 
          component="h2"
          gutterBottom
          sx={{ fontWeight: 500 }}
        >
          Requirements
        </Typography>
        <List 
          component="ul"
          sx={{ 
            listStyleType: 'disc', 
            ml: 3, 
            ...textStyle 
          }}
        >
          {job.JobSkills && job.JobSkills.map((item, index) => (
            <ListItem 
              key={index} 
              component="li"
              sx={{ 
                display: 'list-item', 
                pl: 2,
                py: 0.5
              }}
            >
              {item}
            </ListItem>
          ))}
        </List>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Box display="flex" justifyContent="center">
        <Button
          type="submit"
          variant="contained"
          onClick={() => navigate(`/application/${job.ID}`)}
          sx={{
            borderRadius: '50px',
            backgroundColor: '#0087B4',
            color: '#FFFFFFD9',
            padding: '12px 24px',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 500,
            textTransform: 'none',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: '#006B91',
              boxShadow: 3,
            },
          }}
        >
          Apply Now
          <ArrowForward sx={{ marginLeft: '8px' }} />
        </Button>
      </Box>
    </Container>
  );
};

export default JDPage;